import React, { useState, useContext, useMemo } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { FaUserCheck, FaTimes } from "react-icons/fa";
import { AuthContext } from "../../../auth/AuthContext";
import { ReportContext } from "../../ReportContext";
import Swal from "sweetalert2";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: "500px",
  bgcolor: "#fff",
  boxShadow: 24,
  borderRadius: "16px",
  overflow: "hidden",
  p: 3,
};

export default function AssignAdmin({ issueId, reload, setReload }) {
  const { allAdmins, currentUser } = useContext(AuthContext);
  const { assignSpecialist } = useContext(ReportContext);

  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({
    county: "",
    organisation: "",
    role: "",
    search: "",
  });
  const [selectedAdmin, setSelectedAdmin] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  

  // Filter out support users first
const validAdmins = useMemo(
  () => (allAdmins || []).filter((a) => a.role?.toLowerCase() !== "support"),
  [allAdmins]
);

// Extract unique filter values only from non-support admins
const counties = [...new Set(validAdmins.map((a) => a.county).filter(Boolean))];
const organisations = [
  ...new Set(validAdmins.map((a) => a.organisation).filter(Boolean)),
];
const roles = [...new Set(validAdmins.map((a) => a.role).filter(Boolean))];

// Apply filters based on valid admins
const filteredAdmins = useMemo(() => {
  let filtered = validAdmins;
  if (filters.county)
    filtered = filtered.filter((a) => a.county === filters.county);
  if (filters.organisation)
    filtered = filtered.filter((a) => a.organisation === filters.organisation);
  if (filters.role) filtered = filtered.filter((a) => a.role === filters.role);
  if (filters.search)
    filtered = filtered.filter((a) =>
      a.name.toLowerCase().includes(filters.search.toLowerCase())
    );
  return filtered;
}, [validAdmins, filters]);


  const handleAssignment = async () => {
    if (!selectedAdmin) {
      Swal.fire("Select an Admin", "Please select a specialist to assign.", "warning");
      return;
    }

    if (selectedAdmin === "no_support") {
      await Swal.fire({
        icon: "info",
        title: "No Support Selected",
        text: "This issue will remain unassigned for now.",
        confirmButtonColor: "#6c757d",
      });
      handleClose();
      return;
    }

    const response = await assignSpecialist({
      issue_id: issueId,
      assigned_to: selectedAdmin,
    });

    if (response?.status === "success") {
      setReload(!reload);
      Swal.fire({
        icon: "success",
        title: "Admin Assigned",
        text: "The Admin/Specialist has been successfully assigned.",
        confirmButtonColor: "#28a745",
      });
      handleClose();
    }
  };

  return (
    <>
      <button
        className="btn btn-outline-primary d-flex align-items-center"
        title="Assign Admin"
        onClick={handleOpen}
      >
        <FaUserCheck style={{ marginRight: 6 }} />
        Assign Admin
      </button>

      <Modal open={open} onClose={handleClose}>
        <AnimatePresence>
          <motion.div
            key="assign-modal"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={modalStyle}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Assign Specialist or Authority to Issue
                </Typography>
                <IconButton onClick={handleClose}>
                  <FaTimes />
                </IconButton>
              </Box>

              <Typography variant="body2" color="text.secondary" mb={2}>
                Choose a specialist/authority to handle this issue or Resolve it.
              </Typography>

              {/* Filters Section */}
              <Box display="flex" flexDirection="column" gap={2} mb={2}>
                <TextField
                  label="Search by name"
                  variant="outlined"
                  size="small"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                />

                <Box display="flex" gap={1} flexWrap="wrap">
                  <TextField
                    label="County"
                    select
                    size="small"
                    value={filters.county}
                    onChange={(e) =>
                      setFilters({ ...filters, county: e.target.value })
                    }
                    sx={{ flex: 1, minWidth: "48%" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {counties.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Organisation"
                    select
                    size="small"
                    value={filters.organisation}
                    onChange={(e) =>
                      setFilters({ ...filters, organisation: e.target.value })
                    }
                    sx={{ flex: 1, minWidth: "48%" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {organisations.map((org) => (
                      <MenuItem key={org} value={org}>
                        {org}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Role"
                    select
                    size="small"
                    value={filters.role}
                    onChange={(e) =>
                      setFilters({ ...filters, role: e.target.value })
                    }
                    sx={{ flex: 1, minWidth: "48%" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {roles.map((r) => (
                      <MenuItem key={r} value={r}>
                        {/* {r} */}
                        {r === 'admin' ? "Authority": r}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>

              {/* Admin Select */}
              <TextField
                label="Select Specialist"
                select
                fullWidth
                size="medium"
                value={selectedAdmin}
                onChange={(e) => setSelectedAdmin(e.target.value)}
                sx={{ mb: 3 }}
              >
                <MenuItem value="">-- Choose a Specialist / Authority --</MenuItem>
                {/* <MenuItem value="no_support">No Support</MenuItem> */}
                {filteredAdmins.map((admin) => (
                  <MenuItem key={admin.user_id} value={admin.user_id}>
                    {admin.name}{" "}
                    {admin.user_id === currentUser?.id ? "(You)" : ""} —{" "}
                    {admin.role === 'admin' ? "Authority": admin.role}, {admin.organisation}, {admin.county || "All Counties"}
                  </MenuItem>
                ))}
              </TextField>

              {/* Action Buttons */}
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#007bff" }}
                  onClick={handleAssignment}
                >
                  Assign
                </Button>
              </Box>
            </Box>
          </motion.div>
        </AnimatePresence>
      </Modal>
    </>
  );
}
